import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LogOut, Settings, ChevronDown } from 'lucide-react';

/**
 * Header con menu utente
 */
function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Chiudi menu quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const roleLabels = {
    admin: 'Amministratore',
    operator: 'Operatore',
    client: 'Cliente',
  };

  return (
    <header 
      className="h-14 sm:h-16 bg-card border-b border-border flex items-center justify-between px-3 sm:px-4 md:px-6 sticky top-0 z-30"
      style={{
        paddingRight: 'max(0.75rem, env(safe-area-inset-right))',
      }}
    >
      {/* Titolo pagina (spazio per breadcrumb futuri) */}
      <div className="ml-14 sm:ml-12 md:ml-0 min-w-0 flex-1">
        <h2 className="text-base sm:text-lg font-semibold text-foreground truncate">
          Benvenuto, {user?.firstName}
        </h2>
        <p className="text-xs sm:text-sm text-muted-foreground">{roleLabels[user?.role]}</p>
      </div>

      {/* Menu utente */}
      <div className="relative flex-shrink-0" ref={menuRef}>
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="flex items-center gap-1.5 sm:gap-2 p-1.5 sm:p-2 rounded-lg hover:bg-accent active:bg-accent/80 transition-colors min-h-[44px]"
          aria-expanded={isMenuOpen}
          aria-haspopup="true"
        >
          <div className="w-9 h-9 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-primary font-medium text-sm">
              {user?.firstName?.[0]}
              {user?.lastName?.[0]}
            </span>
          </div>
          <ChevronDown
            size={18}
            className={`text-muted-foreground transition-transform hidden sm:block ${
              isMenuOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {/* Dropdown menu */}
        {isMenuOpen && (
          <>
            {/* Overlay per mobile */}
            <div 
              className="fixed inset-0 z-40 sm:hidden" 
              onClick={() => setIsMenuOpen(false)}
              style={{ touchAction: 'none' }}
            />
            <div 
              className="absolute right-0 mt-2 w-64 sm:w-56 bg-card rounded-lg border border-border shadow-lg py-1 z-50 animate-fadeIn"
              style={{
                right: 'max(0px, calc(env(safe-area-inset-right) - 0.75rem))',
              }}
            >
              {/* Info utente */}
              <div className="px-4 py-3 border-b border-border">
                <p className="font-medium text-sm">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>

              {/* Opzioni menu */}
              <div className="py-1">
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    navigate('/settings');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 sm:py-2 text-sm text-foreground hover:bg-accent active:bg-accent/80 transition-colors min-h-[48px] sm:min-h-[40px]"
                >
                  <Settings size={18} />
                  <span>Impostazioni</span>
                </button>
              </div>

              {/* Logout */}
              <div className="border-t border-border py-1">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 sm:py-2 text-sm text-destructive hover:bg-destructive/10 active:bg-destructive/20 transition-colors min-h-[48px] sm:min-h-[40px]"
                >
                  <LogOut size={18} />
                  <span>Esci</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </header>
  );
}

export default Header;
